import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import BillingAccessCore from './BillingAccessCore';
import { BillingAccess } from '../widget/types';

interface BillingAccessModalProps {
  open: boolean;
  workspaceId: number;
  workspaceName: string;
  onClose: () => void;
}

export default function BillingAccessModal({ open, workspaceId, workspaceName, onClose }: BillingAccessModalProps) {
  const { showSuccess, showError } = useToast();
  const [billingAccess, setBillingAccess] = useState<BillingAccess[]>([]);
  const [loading, setLoading] = useState(true);
  const [closeHovered, setCloseHovered] = useState(false);

  useEffect(() => {
    if (open && workspaceId) {
      loadBillingAccess();
    }
  }, [open, workspaceId]);

  async function loadBillingAccess() {
    try {
      setLoading(true);
      const response = await api.get(`/billing-access/workspaces/${workspaceId}/billing-access`);
      setBillingAccess(response.data);
    } catch (err: any) {
      showError(err.message || 'Failed to load billing admins');
    } finally {
      setLoading(false);
    }
  }

  async function handleGrantAccess(email: string) {
    await api.post(`/billing-access/workspaces/${workspaceId}/billing-access`, {
      pod_user_email: email,
    });
    await loadBillingAccess();
  }

  async function handleRevokeAccess(id: number, email: string) {
    await api.delete(`/billing-access/workspaces/${workspaceId}/billing-access/by-email/${encodeURIComponent(email)}`);
    await loadBillingAccess();
  }

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--v2-surface)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)',
          border: '1px solid var(--v2-border)',
          maxWidth: '720px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'var(--font-sans)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Content */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          <BillingAccessCore
            billingAccess={billingAccess}
            loading={loading}
            error={null}
            mode="modal"
            onGrantAccess={handleGrantAccess}
            onRevokeAccess={handleRevokeAccess}
            onSuccess={showSuccess}
            onError={showError}
          />
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid var(--v2-border)',
            backgroundColor: 'var(--v2-surface-alt)',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={onClose}
            onMouseEnter={() => setCloseHovered(true)}
            onMouseLeave={() => setCloseHovered(false)}
            style={{
              padding: '10px 20px',
              backgroundColor: closeHovered ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
              color: 'var(--v2-text)',
              border: '1px solid var(--v2-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
